// Meta blocked the connected Facebook profile until its owner logs in at facebook.com and clears
// the steps shown there (Graph 190/459, ORP-26180). Reconnecting in Orphex does not help, so this is
// a state of its own rather than another "connection lost" — New-Front's `metaCheckpoint.js`, ported.
//
// Until ORP-26180 v4 read none of it: `need_facebook_checkpoint` arrived on `account_detail` and the
// only thing the user saw was Meta's ordinary reauth line, which sent them to a reconnect that could
// not help.
import { ConnectionLifecycleApi } from '../api/ConnectionLifecycleApi'

import { ApiError } from '@/shared/http'

/** The Health Checks card the backend writes for the same block; its action is the verify route. */
export const META_CHECKPOINT_DIAGNOSTIC_LABEL = 'meta_checkpoint_required'
export const META_CHECKPOINT_REQUIRED = 'META_CHECKPOINT_REQUIRED'
export const FACEBOOK_URL = 'https://www.facebook.com'

/**
 * Who may see the checkpoint UI (ORP-26183). Sysadmins only for now — every other user keeps what
 * they saw before it. Every surface asks THIS question, so releasing it is a one-line change.
 */
export function canSeeMetaCheckpoint(isSysAdmin: boolean): boolean {
  return isSysAdmin
}

function checkpointEntry(accountDetail: Record<string, unknown> | null): Record<string, unknown> {
  const entries = Array.isArray(accountDetail?.reauth_required) ? accountDetail.reauth_required : []
  for (const entry of entries as unknown[]) {
    if (entry && typeof entry === 'object') {
      const record = entry as Record<string, unknown>
      if (record.family === 'meta' && record.reason === 'checkpoint') return record
    }
  }
  return {}
}

/**
 * Whether `account_detail` reports an active Meta checkpoint. `reauth_required` is the uniform
 * signal; `need_facebook_checkpoint` is the legacy boolean the backend publishes alongside it.
 */
export function isMetaCheckpointActive(accountDetail: Record<string, unknown> | null): boolean {
  if (!accountDetail) return false
  if (accountDetail.need_facebook_checkpoint === true) return true
  return Object.keys(checkpointEntry(accountDetail)).length > 0
}

/**
 * The blocked Facebook profile, as the copy names it: the checkpoint entry's `connected_identity`
 * ("Name (id)" / "Name (email)", ORP-27552), else `meta_email` (which holds the bare id when Meta
 * withheld the address), else `null` for the generic wording.
 */
export function metaCheckpointIdentity(
  accountDetail: Record<string, unknown> | null,
): string | null {
  for (const value of [
    checkpointEntry(accountDetail).connected_identity,
    accountDetail?.meta_email,
  ]) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

/**
 * What a verify answered:
 *   cleared — Meta answered, the block is gone;
 *   blocked — Meta still shows it (409 `META_CHECKPOINT_REQUIRED`);
 *   reauth  — the token is dead or missing, so the normal reconnect line takes over;
 *   failed  — anything else; `detail` is the backend's sentence when it marked one user-visible.
 */
export type MetaCheckpointVerifyOutcome =
  | { status: 'cleared' | 'reauth' | 'blocked' }
  | { status: 'failed'; detail: string | null }
  | { status: 'canceled' }

export function describeVerifyFailure(cause: unknown): MetaCheckpointVerifyOutcome {
  const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  if (error.kind === 'canceled') return { status: 'canceled' }
  if (error.code === META_CHECKPOINT_REQUIRED) return { status: 'blocked' }
  if (error.code === 'INTEGRATION_AUTH_REQUIRED') return { status: 'reauth' }
  // Only a sentence the backend marked for the user is shown; `ApiError.userVisible` defaults to
  // true for a body that says nothing, which is not the same claim.
  const payload = error.payload as { error?: { user_visible?: unknown; message?: unknown } } | null
  const message = payload?.error?.message
  const visible = payload?.error?.user_visible === true && typeof message === 'string'
  return { status: 'failed', detail: visible && message.trim() ? message.trim() : null }
}

/** Never throws: the caller re-reads `account_detail` unless the block remains. */
export async function verifyMetaCheckpoint(path: string): Promise<MetaCheckpointVerifyOutcome> {
  try {
    await ConnectionLifecycleApi.verifyMetaCheckpoint(path)
    return { status: 'cleared' }
  } catch (cause) {
    return describeVerifyFailure(cause)
  }
}
